import React, { createContext, useContext, useState, useEffect } from "react";
import { decodeTokenService } from "../services/decodeTokenService";
import { Language } from "../utils/dictionary";

interface AuthContextType {
  setLanguage: (language: Language) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (access_token: string, refresh_token: string) => void;
  logout: () => void;
  refreshAuth: () => void;
  isUserFake: boolean;
  language: Language;
  credits: number;
  setCredits: (credits:number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [isUserFake, setIsUserFake] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Prevent premature redirects
  const [language, setLanguage] = useState<Language>(Language.SPANISH);
  const [credits, setCredits] = useState<number>(0);

  const checkAuthentication = () => {
    setIsLoading(true);
    try {
      const access_token = localStorage.getItem("access_token");
      const refresh_token = localStorage.getItem("refresh_token");

      if (access_token || refresh_token) {
        const user_id = decodeTokenService.getUserIdFromToken()
        
        if (user_id === import.meta.env.VITE_REACT_APP_USER_ID_FAKE) {
          setIsUserFake(true);
        }
        setIsAuthenticated(true);
      
      } else {
        setIsAuthenticated(false);
      }

    } catch (error) {
      console.error("Error validating authentication:", error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (access_token: string, refresh_token: string) => {
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setIsAuthenticated(false);
  };

  const refreshAuth = () => {
    checkAuthentication();
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  if (isLoading) {
    return <div>Loading Authentication...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshAuth,
        isUserFake,
        language,
        setLanguage,
        credits,
        setCredits,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
